import { z } from 'zod';

export const ANALYTICS_EVENT_TYPES = [
  'tab_click',
  'catalog_view',
  'catalog_search',
  'club_open',
  'read_open',
  'chapter_complete',
  'paywall_open',
  'entitlement_granted',
  'install_prompt_impression',
  'install_prompt_click',
  'install_prompt_dismiss',
  'install_guide_open',
  'install_guide_platform_switch',
] as const;

export type AnalyticsEventType = (typeof ANALYTICS_EVENT_TYPES)[number];

export const PUBLIC_ANALYTICS_EVENT_TYPES = [
  'install_prompt_impression',
  'install_prompt_click',
  'install_prompt_dismiss',
  'install_guide_open',
  'install_guide_platform_switch',
] as const satisfies readonly AnalyticsEventType[];

export type PublicAnalyticsEventType = (typeof PUBLIC_ANALYTICS_EVENT_TYPES)[number];

export const PUBLIC_ANALYTICS_EVENT_TYPE_SET = new Set<AnalyticsEventType>(
  PUBLIC_ANALYTICS_EVENT_TYPES,
);

export function isPublicAnalyticsEventType(type: string): type is PublicAnalyticsEventType {
  return PUBLIC_ANALYTICS_EVENT_TYPE_SET.has(type as AnalyticsEventType);
}

export const AnalyticsEventItemSchema = z.object({
  type: z.string().min(1).max(100),
  payload: z.record(z.unknown()).optional(),
  timestamp: z.string().datetime().optional(),
  sessionId: z.string().max(64).optional(),
});
export type AnalyticsEventItem = z.infer<typeof AnalyticsEventItemSchema>;

export const AnalyticsIngestSchema = z.object({
  events: z.array(AnalyticsEventItemSchema).min(1).max(50),
});
export type AnalyticsIngestDto = z.infer<typeof AnalyticsIngestSchema>;

export const AnalyticsIngestResultSchema = z.object({
  accepted: z.number(),
});
export type AnalyticsIngestResult = z.infer<typeof AnalyticsIngestResultSchema>;

export const AdminAnalyticsTimestampSchema = z.string().datetime();

export const AdminPaymentsAnalyticsStatusSchema = z.enum(['pending', 'paid', 'failed', 'expired']);
export type AdminPaymentsAnalyticsStatus = z.infer<typeof AdminPaymentsAnalyticsStatusSchema>;

export const AdminPaymentsAnalyticsQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  clubId: z.string().min(1).optional(),
  status: AdminPaymentsAnalyticsStatusSchema.optional(),
  q: z.string().trim().min(1).max(200).optional(),
});
export type AdminPaymentsAnalyticsQuery = z.infer<typeof AdminPaymentsAnalyticsQuerySchema>;

export const AdminPaymentsAnalyticsSummarySchema = z.object({
  paidRevenue: z.number().int().nonnegative(),
  paidOrdersCount: z.number().int().nonnegative(),
  uniquePayersCount: z.number().int().nonnegative(),
  averageOrderValue: z.number().int().nonnegative(),
  repeatPayersCount: z.number().int().nonnegative(),
  repeatPayersRate: z.number().min(0).max(1),
  failedOrdersCount: z.number().int().nonnegative(),
  pendingOrdersCount: z.number().int().nonnegative(),
});
export type AdminPaymentsAnalyticsSummary = z.infer<typeof AdminPaymentsAnalyticsSummarySchema>;

export const AdminPaymentsAnalyticsPeriodAggregateSchema = z.object({
  key: z.enum(['day', 'week', 'month', 'total']),
  label: z.string().min(1),
  paidRevenue: z.number().int().nonnegative(),
  paidOrdersCount: z.number().int().nonnegative(),
  uniquePayersCount: z.number().int().nonnegative(),
});
export type AdminPaymentsAnalyticsPeriodAggregate = z.infer<
  typeof AdminPaymentsAnalyticsPeriodAggregateSchema
>;

export const AdminPaymentsAnalyticsClubAggregateSchema = z.object({
  clubId: z.string(),
  clubTitle: z.string().min(1),
  paidRevenue: z.number().int().nonnegative(),
  paidOrdersCount: z.number().int().nonnegative(),
  uniquePayersCount: z.number().int().nonnegative(),
  averageOrderValue: z.number().int().nonnegative(),
});
export type AdminPaymentsAnalyticsClubAggregate = z.infer<
  typeof AdminPaymentsAnalyticsClubAggregateSchema
>;

export const AdminPaymentsAnalyticsJournalRowSchema = z.object({
  orderId: z.string(),
  paymentTimestamp: AdminAnalyticsTimestampSchema,
  userId: z.string(),
  userLabel: z.string().min(1),
  payerEmail: z.string(),
  clubId: z.string(),
  clubTitle: z.string().min(1),
  amount: z.number().int().nonnegative(),
  currency: z.string().min(1).max(16),
  status: AdminPaymentsAnalyticsStatusSchema,
  providerInvoiceId: z.string(),
});
export type AdminPaymentsAnalyticsJournalRow = z.infer<
  typeof AdminPaymentsAnalyticsJournalRowSchema
>;

export const AdminPaymentsAnalyticsResponseSchema = z.object({
  generatedAt: AdminAnalyticsTimestampSchema,
  filters: z.object({
    from: z.string().datetime().nullable(),
    to: z.string().datetime().nullable(),
    clubId: z.string().nullable(),
    status: AdminPaymentsAnalyticsStatusSchema.nullable(),
    q: z.string().nullable(),
  }),
  summary: AdminPaymentsAnalyticsSummarySchema,
  periodAggregates: z.array(AdminPaymentsAnalyticsPeriodAggregateSchema),
  clubAggregates: z.array(AdminPaymentsAnalyticsClubAggregateSchema),
  journal: z.array(AdminPaymentsAnalyticsJournalRowSchema),
});
export type AdminPaymentsAnalyticsResponse = z.infer<typeof AdminPaymentsAnalyticsResponseSchema>;

export const AdminRetentionChartRangeSchema = z.enum(['7d', '30d', '3m']);
export type AdminRetentionChartRange = z.infer<typeof AdminRetentionChartRangeSchema>;

export const AdminRetentionAnalyticsQuerySchema = z.object({
  clubId: z.string().min(1).optional(),
  chartRange: AdminRetentionChartRangeSchema.optional(),
});
export type AdminRetentionAnalyticsQuery = z.infer<typeof AdminRetentionAnalyticsQuerySchema>;

export const AdminRetentionAnalyticsSummarySchema = z.object({
  usersWithAccess: z.number().int().nonnegative(),
  usersWithReadingStarted: z.number().int().nonnegative(),
  activeReaders1d: z.number().int().nonnegative(),
  activeReaders7d: z.number().int().nonnegative(),
  activeReaders30d: z.number().int().nonnegative(),
  deepReaders80Count: z.number().int().nonnegative(),
  averageProgressPercent: z.number().min(0).max(100),
  medianProgressPercent: z.number().min(0).max(100),
});
export type AdminRetentionAnalyticsSummary = z.infer<typeof AdminRetentionAnalyticsSummarySchema>;

export const AdminRetentionRegistrationsSchema = z.object({
  registeredUsersCount: z.number().int().nonnegative(),
  registeredUsersWithAccessCount: z.number().int().nonnegative(),
});
export type AdminRetentionRegistrations = z.infer<typeof AdminRetentionRegistrationsSchema>;

export const AdminRetentionActivationSchema = z.object({
  accessUsersCount: z.number().int().nonnegative(),
  startedFromAccessCount: z.number().int().nonnegative(),
  accessToFirstReadRate: z.number().min(0).max(1),
  paidUsersCount: z.number().int().nonnegative(),
  startedFromPaidCount: z.number().int().nonnegative(),
  paidToFirstReadRate: z.number().min(0).max(1),
  averagePaymentToFirstReadHours: z.number().nonnegative().nullable(),
  medianPaymentToFirstReadHours: z.number().nonnegative().nullable(),
});
export type AdminRetentionActivation = z.infer<typeof AdminRetentionActivationSchema>;

export const AdminRetentionFrequencyBucketSchema = z.object({
  key: z.enum(['1', '2-3', '4-7', '8-14', '15+']),
  label: z.string().min(1),
  usersCount: z.number().int().nonnegative(),
});
export type AdminRetentionFrequencyBucket = z.infer<typeof AdminRetentionFrequencyBucketSchema>;

export const AdminRetentionClubRowSchema = z.object({
  clubId: z.string(),
  clubTitle: z.string().min(1),
  usersWithAccess: z.number().int().nonnegative(),
  usersWithReadingStarted: z.number().int().nonnegative(),
  reached25Count: z.number().int().nonnegative(),
  reached50Count: z.number().int().nonnegative(),
  reached80Count: z.number().int().nonnegative(),
  reached100Count: z.number().int().nonnegative(),
  averageProgressPercent: z.number().min(0).max(100),
  medianProgressPercent: z.number().min(0).max(100),
  averageCompletedChapters: z.number().nonnegative(),
  activeReaders7d: z.number().int().nonnegative(),
  activeReaders30d: z.number().int().nonnegative(),
});
export type AdminRetentionClubRow = z.infer<typeof AdminRetentionClubRowSchema>;

export const AdminRetentionChartPointSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  label: z.string().min(1),
  value: z.number().int().nonnegative(),
});
export type AdminRetentionChartPoint = z.infer<typeof AdminRetentionChartPointSchema>;

export const AdminRetentionClubAccessSeriesSchema = z.object({
  clubId: z.string(),
  clubTitle: z.string().min(1),
  points: z.array(AdminRetentionChartPointSchema),
});
export type AdminRetentionClubAccessSeries = z.infer<typeof AdminRetentionClubAccessSeriesSchema>;

export const AdminRetentionAnalyticsResponseSchema = z.object({
  generatedAt: AdminAnalyticsTimestampSchema,
  filters: z.object({
    clubId: z.string().nullable(),
    chartRange: AdminRetentionChartRangeSchema,
  }),
  charts: z.object({
    registeredUsers: z.array(AdminRetentionChartPointSchema),
    accessByClub: z.array(AdminRetentionClubAccessSeriesSchema),
  }),
  registrations: AdminRetentionRegistrationsSchema,
  summary: AdminRetentionAnalyticsSummarySchema,
  activation: AdminRetentionActivationSchema,
  depthThresholds: z.object({
    reached25Count: z.number().int().nonnegative(),
    reached50Count: z.number().int().nonnegative(),
    reached80Count: z.number().int().nonnegative(),
    reached100Count: z.number().int().nonnegative(),
  }),
  frequencyBuckets: z.array(AdminRetentionFrequencyBucketSchema),
  clubRows: z.array(AdminRetentionClubRowSchema),
});
export type AdminRetentionAnalyticsResponse = z.infer<typeof AdminRetentionAnalyticsResponseSchema>;
